import angular from "angular";
import { AuthService } from "./services/AuthService";
import { CaseService } from "./services/CaseService";
import { CaseSummaryService } from "./services/CaseSummaryService";
import { BridgingDealService } from "./services/Deal/BridgingDealService";
import { CommercialDealService } from "./services/Deal/CommercialDealService";
import { DealLenderService } from "./services/Deal/DealLenderService";
import { DevFinanceDealService } from "./services/Deal/DevFinanceDealService";
import { SharedCriteriaService } from "./services/Deal/SharedCriteriaService";
import { SharedDealService } from "./services/Deal/SharedDealService";
import { SharedSearchResultsService } from "./services/Deal/SharedSearchResultsService";
import { DevelopmentInputService } from "./services/DevelopmentInputService";
import { FeedBackService } from "./services/FeedBackService";
import { FileAttachmentHelper } from "./helpers/FileAttachmentHelper";
import { ModalService } from "./services/ModalService";
import { OrganisationService } from "./services/OrganisationService";
import { RoleService } from "./services/RoleService";
import { SelectListService } from "./services/SelectListService";
import { SharedDataService } from "./services/SharedDataService";
import { UserService } from "./services/UserService";
import { Utils } from "./Utils";
import {EnterpriseService} from "./services/EnterpriseService"
import {UserDashboardService} from "./services/UserDashboardService"
import {SharedHeadsOfTermsService} from "./services/Deal/SharedHeadsOfTermsService"
import { DealFileAttachmentHelper } from "./helpers/DealFileAttachmentHelper";
import {
  AccountClient,
  AppFeatureClient,
  ApplicationUserClient,
  AppPackageClient,
  AppPackageFeatureClient,
  AppPackagePricingClient,
  AppUserPermissionClient,
  AssetLiabilityItemClient,
  AuditLogClient,
  AuthClient,
  BrickflowEventLogClient,
  BridgingDealClient,
  BridgingSearchClient,
  BridgingSearchCriteriaClient,
  CaseClient,
  CaseLenderClient,
  CaseLenderMessageClient,
  CaseLenderMessageTrackClient,
  CaseMemberClient,
  CaseNoteClient,
  ClaimClient,
  ClientClient,
  CommercialDealClient,
  CommercialSearchResultsClient,
  ComparablePropertyClient,
  DashboardClient,
  DealClient,
  DealClientClient,
  DealCompletionClient,
  DealFileAttachmentClient,
  DealLenderClient,
  DealLenderMessageClient,
  DealLenderMessageTrackClient,
  DealNoteClient,
  DealScheduleClient,
  DeveloperExitFinanceDevScheduleInputClient,
  DeveloperExitFinanceLoanInputClient,
  DevelopmentInputClient,
  DevelopmentInputScheduleClient,
  DevelopmentInputWithNoLoginClient,
  DevelopmentTrackRecordClient,
  DevelopmentFinanceDealClient,
  DevFinanceSearchClient,
  ExternalApiClient,
  ExternalLinksClient,
  FeedBackClient,
  FileAttachmentClient,
  IntroducerClient,
  IntroducerSimpleClient,
  InviteClient,
  LenderClient,
  LenderOrganisationClient,
  LenderResultClient,
  LicenseAppPackageClient,
  LicenseMasterClient,
  MagicLinkClient,
  MetadataClient,
  OrganisationClient,
  OrganisationLinkClient,
  OTPClient,
  PaymentClient,
  PostcodeLocationClient,
  ProductClient,
  ProductRateClient,
  ProfileClient,
  ProfileAssetLiabilityItemClient,
  ProjectOwnershipClient,
  QualificationsClient,
  RoleClient,
  ScheduledJobLogClient,
  SelectedResultClient,
  SnapshotLenderClient,
  SnapshotProductClient,
  SnapshotSearchResultClient,
  StaticDataClient,
  StreetAddressClient,
  TaskItemClient,
  TeamMemberClient,
  ThirdPartyApiAccessKeyClient,
  UserClient,
  ApplicationUserCrudClient,
  OrganisationCrudClient,
  DealNoteCrudClient, DevelopmentFinanceDealCrudClient, DealCrudClient, CaseCrudClient,
  ProjectOwnershipCrudClient,
  CaseMemberCrudClient,
  DealLenderCrudClient,
  AssetLiabilityItemCrudClient, DevelopmentInputCrudClient,FileAttachmentCrudClient, 
  DevelopmentTrackRecordCrudClient,
  OrganisationLinkCrudClient
} from "@src/apis/ModularClient";
import { IndexService } from "@js/services/IndexService";
import { ODataHelper } from "@js/helpers/ODataHelper";

const createAuthorizedFetch = (accessToken: string) => {
  const authorizedFetch = (
    url: RequestInfo,
    init?: RequestInit,
  ): Promise<Response> => {
      const authorizedHeaders = !accessToken
          ? init?.headers
          : {
              ...init?.headers,
              Authorization: `Bearer ${accessToken}`,
          };

      const authorizedInit: RequestInit = {
          ...init,
          headers: authorizedHeaders,
      };

      return fetch(url, authorizedInit);
  };

  return authorizedFetch;
}

function createClientFactory(ClientClass: any) {
    return ['baseUrl', '$http', '$q', function(baseUrl: string, $http: ng.IHttpService, $q: ng.IQService) {
        const client = new ClientClass($http, $q, baseUrl);
        // Add the date reviver to all clients
        client.jsonParseReviver = (key: string, value: any): any => {
            const dateFormat = /(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2})/;
            if (typeof value === 'string' && dateFormat.test(value)) {              
                const date = new Date(value);
                return isNaN(date.getTime()) ? value : date;
            }
            return value;
        };
        return client;
    }];
}

function registerAllServices(apiClients: { [key: string]: any }, regularServices: { [key: string]: any }) {
    const module = angular.module('ccqapp');

    // Register baseUrl constant
    module.factory('baseUrl', ['$location', function($location) {
      return $location.protocol() + '://' + $location.host() +
        ($location.port() ? ':' + $location.port() : '');
    }]);

    // Register API clients with factory
    Object.entries(apiClients).forEach(([clientName, ClientClass]) => {
        module.service(clientName, createClientFactory(ClientClass));
    });

    // Register regular services directly
    Object.entries(regularServices).forEach(([serviceName, ServiceClass]) => {
        module.service(serviceName, ServiceClass);
    });
}

export function registerServices() {
    const apiClients = {
        'AccountClient': AccountClient,
        'AppFeatureClient': AppFeatureClient,
        'ApplicationUserClient': ApplicationUserClient,
        'AppPackageClient': AppPackageClient,
        'AppPackageFeatureClient': AppPackageFeatureClient,
        'AppPackagePricingClient': AppPackagePricingClient,
        'AppUserPermissionClient': AppUserPermissionClient,
        'AssetLiabilityItemClient': AssetLiabilityItemClient,
        'AuditLogClient': AuditLogClient,
        'AuthClient': AuthClient,
        'BrickflowEventLogClient': BrickflowEventLogClient,
        'BridgingDealClient': BridgingDealClient,
        'BridgingSearchClient': BridgingSearchClient,
        'BridgingSearchCriteriaClient': BridgingSearchCriteriaClient,
        'CaseClient': CaseClient,
        'CaseLenderClient': CaseLenderClient,
        'CaseLenderMessageClient': CaseLenderMessageClient,
        'CaseLenderMessageTrackClient': CaseLenderMessageTrackClient,
        'CaseMemberClient': CaseMemberClient,
        'CaseNoteClient': CaseNoteClient,
        'ClaimClient': ClaimClient,
        'ClientClient': ClientClient,
        'CommercialDealClient': CommercialDealClient,
        'CommercialSearchResultsClient': CommercialSearchResultsClient,
        'ComparablePropertyClient': ComparablePropertyClient,
        'DashboardClient': DashboardClient,
        'DealClient': DealClient,
        'DealClientClient': DealClientClient,
        'DealCompletionClient': DealCompletionClient,
        'DealNoteCrudClient': DealNoteCrudClient,
        'DealFileAttachmentClient': DealFileAttachmentClient,
        'DealLenderClient': DealLenderClient,
        'DealLenderMessageClient': DealLenderMessageClient,
        'DealLenderMessageTrackClient': DealLenderMessageTrackClient,
        'DealNoteClient': DealNoteClient,
        'DealScheduleClient': DealScheduleClient,
        'DeveloperExitFinanceDevScheduleInputClient': DeveloperExitFinanceDevScheduleInputClient,
        'DeveloperExitFinanceLoanInputClient': DeveloperExitFinanceLoanInputClient,
        'DevelopmentInputClient': DevelopmentInputClient,
        'DevelopmentInputScheduleClient': DevelopmentInputScheduleClient,
        'DevelopmentInputWithNoLoginClient': DevelopmentInputWithNoLoginClient,
        'DevelopmentTrackRecordClient': DevelopmentTrackRecordClient,
        'DevelopmentFinanceDealClient': DevelopmentFinanceDealClient,
        'DevFinanceSearchClient': DevFinanceSearchClient,
        'ExternalApiClient': ExternalApiClient,
        'ExternalLinksClient': ExternalLinksClient,
        'FeedBackClient': FeedBackClient,
        'FileAttachmentClient': FileAttachmentClient,
        'IntroducerClient': IntroducerClient,
        'IntroducerSimpleClient': IntroducerSimpleClient,
        'InviteClient': InviteClient,
        'LenderClient': LenderClient,
        'LenderOrganisationClient': LenderOrganisationClient,
        'LenderResultClient': LenderResultClient,
        'LicenseAppPackageClient': LicenseAppPackageClient,
        'LicenseMasterClient': LicenseMasterClient,
        'MagicLinkClient': MagicLinkClient,
        'MetadataClient': MetadataClient,
        'OrganisationClient': OrganisationClient,
        'OrganisationLinkClient': OrganisationLinkClient,
        'OTPClient': OTPClient,
        'PaymentClient': PaymentClient,
        'PostcodeLocationClient': PostcodeLocationClient,
        'ProductClient': ProductClient,
        'ProductRateClient': ProductRateClient,
        'ProfileClient': ProfileClient,
        'ProfileAssetLiabilityItemClient': ProfileAssetLiabilityItemClient,
        'ProjectOwnershipClient': ProjectOwnershipClient,
        'QualificationsClient': QualificationsClient,
        'RoleClient': RoleClient,
        'ScheduledJobLogClient': ScheduledJobLogClient,
        'SelectedResultClient': SelectedResultClient,
        'SnapshotLenderClient': SnapshotLenderClient,
        'SnapshotProductClient': SnapshotProductClient,
        'SnapshotSearchResultClient': SnapshotSearchResultClient,
        'StaticDataClient': StaticDataClient,
        'StreetAddressClient': StreetAddressClient,
        'TaskItemClient': TaskItemClient,
        'TeamMemberClient': TeamMemberClient,
        'ThirdPartyApiAccessKeyClient': ThirdPartyApiAccessKeyClient,
        'UserClient': UserClient,
        'ApplicationUserCrudClient': ApplicationUserCrudClient,
        'DevelopmentFinanceDealCrudClient': DevelopmentFinanceDealCrudClient,
        'DealCrudClient': DealCrudClient,
        'OrganisationCrudClient': OrganisationCrudClient,
        'CaseCrudClient': CaseCrudClient,
        'ProjectOwnershipCrudClient':ProjectOwnershipCrudClient,
        'CaseMemberCrudClient':CaseMemberCrudClient,
        'DealLenderCrudClient': DealLenderCrudClient,
        'AssetLiabilityItemCrudClient': AssetLiabilityItemCrudClient,
        'DevelopmentInputCrudClient': DevelopmentInputCrudClient,
        'FileAttachmentCrudClient': FileAttachmentCrudClient,
        'DevelopmentTrackRecordCrudClient': DevelopmentTrackRecordCrudClient,
        'OrganisationLinkCrudClient': OrganisationLinkCrudClient
    };

    const regularServices = {
        "AuthService": AuthService,
        "CaseService": CaseService,
        "CaseSummaryService": CaseSummaryService,
        "DealFileAttachmentHelper": DealFileAttachmentHelper,
        "DevelopmentInputService": DevelopmentInputService,
        "FeedBackService": FeedBackService,
        "FileAttachmentHelper": FileAttachmentHelper,
        "ModalService": ModalService,
        "OrganisationService": OrganisationService,
        "RoleService": RoleService,
        "SelectListService": SelectListService,
        "SharedDataService": SharedDataService,
        "UserService": UserService,
        "BridgingDealService": BridgingDealService,
        "CommercialDealService": CommercialDealService,
        "DealLenderService": DealLenderService,
        "DevFinanceDealService": DevFinanceDealService,
        "SharedCriteriaService": SharedCriteriaService,
        "SharedDealService": SharedDealService,
        "SharedSearchResultsService": SharedSearchResultsService,
        "Util": Utils,
        "EnterpriseService": EnterpriseService,
        "UserDashboardService": UserDashboardService,
        "SharedHeadsOfTermsService": SharedHeadsOfTermsService,
        'IndexService': IndexService,
      'ODataHelper': ODataHelper,
    }

    registerAllServices(apiClients, regularServices);
}